import { use, type ReactNode } from 'react';
import type { KnobDef } from './schema/components';
import { useConstructor } from './store/useConstructor';
import { resolveKnob } from './store/resolveTokens';
import { ConstructorTemplateContext } from './store/ConstructorTemplate';
import { ShadowEditor } from './ShadowEditor';
import { ColorField } from './ColorField';

export interface KnobControlProps {
  componentName: string;
  knobName: string;
  knob: KnobDef;
}

const NUMERIC_KINDS = new Set(['radius', 'number', 'opacity', 'fontSize', 'fontWeight']);

export function KnobControl({ componentName, knobName, knob }: KnobControlProps) {
  const { state, setOverride } = useConstructor();
  const template = use(ConstructorTemplateContext);
  const resolved = resolveKnob(state.value, componentName, knobName, template) ?? '';
  const override = state.value.overrides?.[componentName]?.[knobName];
  const hasOverride = override !== undefined;

  if (knob.kind === 'color') {
    return (
      <ColorField
        label={knob.label}
        resolved={resolved}
        override={typeof override === 'string' ? override : ''}
        onChange={(next) => setOverride(componentName, knobName, next)}
        hasOverride={hasOverride}
      />
    );
  }

  if (knob.kind === 'shadow') {
    return (
      <ShadowEditor
        label={knob.label}
        resolved={resolved}
        hasOverride={hasOverride}
        onChange={(next) => setOverride(componentName, knobName, next)}
      />
    );
  }

  if (knob.options && knob.options.length > 0) {
    return (
      <SelectRow
        label={knob.label}
        resolved={resolved}
        options={knob.options}
        override={typeof override === 'string' ? override : ''}
        onChange={(next) => setOverride(componentName, knobName, next === '' ? undefined : next)}
        hasOverride={hasOverride}
      />
    );
  }

  if (NUMERIC_KINDS.has(knob.kind)) {
    return (
      <NumberRow
        label={knob.label}
        resolved={resolved}
        override={typeof override === 'number' ? String(override) : ''}
        onChange={(next) => {
          if (next === undefined) {
            setOverride(componentName, knobName, undefined);
          } else {
            const n = Number(next);
            if (!Number.isNaN(n)) setOverride(componentName, knobName, n);
          }
        }}
        hasOverride={hasOverride}
      />
    );
  }

  return (
    <TextRow
      label={knob.label}
      resolved={resolved}
      override={typeof override === 'string' ? override : ''}
      onChange={(next) => setOverride(componentName, knobName, next)}
      hasOverride={hasOverride}
    />
  );
}

interface RowProps {
  label: string;
  resolved: string;
  override: string;
  onChange: (next: string | number | undefined) => void;
  hasOverride: boolean;
}

interface KnobRowProps {
  label: string;
  hasOverride: boolean;
  onReset: () => void;
  children: ReactNode;
}

/**
 * Two-line row: label + reset on top, the control full-width below. Same
 * layout ColorField uses, and for the same reason — in the narrow sidebar a
 * single-line `w-32` label column either truncated the label down to
 * "BAR O…" or (with `shrink-0`) pushed the reset button out of view.
 */
function KnobRow({ label, hasOverride, onReset, children }: KnobRowProps) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between gap-2">
        <span
          title={label}
          className="truncate font-mono text-[10px] uppercase tracking-wide text-neutral-400"
        >
          {label}
        </span>
        {hasOverride && (
          <button
            type="button"
            aria-label={`Reset ${label}`}
            onClick={onReset}
            className="shrink-0 rounded border border-neutral-700 px-2 py-0.5 text-[10px] text-neutral-400 hover:text-white"
          >
            reset
          </button>
        )}
      </div>
      {children}
    </div>
  );
}

const FIELD_CLASS =
  'w-full rounded border border-neutral-700 bg-neutral-900 px-2 py-1 font-mono text-[12px] text-white outline-none';

function NumberRow({ label, resolved, override, onChange, hasOverride }: RowProps) {
  return (
    <KnobRow label={label} hasOverride={hasOverride} onReset={() => onChange(undefined)}>
      <input
        aria-label={label}
        type="number"
        className={FIELD_CLASS}
        placeholder={resolved}
        value={override}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
      />
    </KnobRow>
  );
}

interface SelectRowProps extends RowProps {
  options: readonly string[];
}

function SelectRow({ label, resolved, override, options, onChange, hasOverride }: SelectRowProps) {
  // Inline font-preview: render each option in its own family so the operator
  // sees the actual face before picking. For non-font enums this just renders
  // the option text in the default UI font — harmless.
  return (
    <KnobRow label={label} hasOverride={hasOverride} onReset={() => onChange(undefined)}>
      <select
        aria-label={label}
        className={FIELD_CLASS}
        value={override || resolved || ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
        style={{ fontFamily: override || resolved || undefined }}
      >
        {!options.includes(override) && override === '' && <option value="">— default —</option>}
        {options.map((opt) => (
          <option key={opt} value={opt} style={{ fontFamily: opt }}>
            {opt}
          </option>
        ))}
      </select>
    </KnobRow>
  );
}

function TextRow({ label, resolved, override, onChange, hasOverride }: RowProps) {
  return (
    <KnobRow label={label} hasOverride={hasOverride} onReset={() => onChange(undefined)}>
      <input
        aria-label={label}
        type="text"
        className={FIELD_CLASS}
        placeholder={resolved}
        value={override}
        onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value)}
      />
    </KnobRow>
  );
}
